import styled, { css } from 'styled-components';
import { fontBody } from 'Styles/fonts';
import breakpoint from 'Styles/breakpoints';
import ButtonIcon from '../Icons/ButtonIcon';
import { Layout } from '../../styles/Layout.styled';
import ContainerQuery from '../ContainerQuery/ContainerQuery';

export const PEEKING_TOP = 24;
const BASE_TOP_MODAL = 0;

const maxWidthMap = {
  mini: '375px',
  regular: '720px',
  mega: '976px',
  xMega: '1520px'
};

const minWidthMap = {
  mini: '320px',
  regular: '320px',
  mega: '320px',
  xMega: '768px'
};

const sideMarginsMap = {
  mini: '24px',
  regular: '24px',
  mega: '24px',
  xMega: '48px'
};

const heightMap = {
  mini: '95%',
  regular: '95%',
  mega: '95%',
  xMega: '100%'
};

const marginTopMap = {
  mini: '0',
  regular: '0',
  mega: '0',
  xMega: '24px'
};

export const Modal = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  z-index: 1301;
  right: 0;
  bottom: 0;
  top: ${BASE_TOP_MODAL}px;
  left: 0;

  ${({ withPeeking }) =>
    withPeeking &&
    css`
      ${breakpoint('medium', { type: 'down' })} {
        opacity: 1;
        top: 100%;
        transition: top ${({ transitionDuration }) => transitionDuration}ms
          cubic-bezier(0.25, 1, 0.25, 1);

        .fade-appear &,
        .fade-enter & {
          top: 100%;
          opacity: unset;
        }
        .fade-appear-active &,
        .fade-enter-active &,
        .fade-enter-done & {
          top: ${PEEKING_TOP}px;
          opacity: unset;
        }
        .fade-exit & {
          top: ${PEEKING_TOP}px;
          opacity: unset;
        }
        .fade-exit-active & {
          top: 100%;
          opacity: unset;
        }
      }
    `}
`;

export const BackButton = styled(ButtonIcon).attrs({
  variant: 'defaultLight',
  name: 'chevron-left'
})`
  ${fontBody(20)};
  left: 4px;
`;

export const CloseButton = styled(ButtonIcon).attrs({
  variant: 'secondary',
  isGhost: true,
  name: 'close'
})`
  ${fontBody(20)};
  right: 4px;
  z-index: 1;
  &:hover, &:focus, &:active {
    background-color: transparent;
  }
`;

export const Container = styled(ContainerQuery)`
  display: flex;
  flex-direction: column;
  position: relative;
  background: white;
  width: 100%;
  height: 100%;
  transition: transform ${({ transitionDuration }) => transitionDuration}ms
    cubic-bezier(0.1, 0.5, 0.1, 1);

  .fade-appear &,
  .fade-enter & {
    transform: scale(0.9);
  }
  .fade-appear-active &,
  .fade-enter-active & {
    transform: scale(1);
  }
  .fade-exit & {
    transform: scale(1);
  }
  .fade-exit-active & {
    transform: scale(0.9);
  }

  ${({ withPeeking }) =>
    withPeeking &&
    css`
      ${breakpoint('medium', { type: 'down' })} {
        border-radius: 8px 8px 0px 0px;
        box-shadow: 0px -4px 6px 0 rgba(0, 0, 0, 0.16);
        transition: none;
        overflow: hidden;
        /* Fixes iOS Safari container overflow not clipping */
        z-index: 1;

        .fade-appear &,
        .fade-enter &,
        .fade-appear-active &,
        .fade-enter-active &,
        .fade-exit &,
        .fade-exit-active & {
          transform: unset;
        }
      }
    `}

  ${breakpoint('medium')} {
    ${({ isFullscreen, size, height }) =>
      isFullscreen
        ? `
            height: 100vh;
            width: 100vw;
          `
        : `
            height: ${heightMap[size] === heightMap.xMega ? '100vh' : height};
            min-width: ${minWidthMap[size]};
            max-width: ${maxWidthMap[size]};
            max-height: ${heightMap[size]};
            border-radius: 8px;
            overflow: hidden;
            margin-left: ${sideMarginsMap[size]};
            margin-right: ${sideMarginsMap[size]};
            margin-top: ${marginTopMap[size]};
            /**
             * hack for fixing border-radius on Safari
             * @see {@link https://stackoverflow.com/questions/49066011/overflow-hidden-with-border-radius-not-working-on-safari}
             */
            transform: translateZ(0);
          `}
  }
`;

const calculateHeaderHeight =
  (max) =>
  ({ withCollapse, hasSlideOptions }) => {
    let totalHeight = 0;
    if (!withCollapse) totalHeight += 64;
    if (hasSlideOptions) totalHeight += 72;
    if (totalHeight) return `calc(${max} - ${totalHeight}px)`;
    return max;
  };

export const Content = styled.div`
  position: relative;
  ${({ isFullscreen, withPadding, mediumLayoutTopBottomPadding }) =>
    css`
      ${!isFullscreen && withPadding && 'padding: 24px'};
      ${breakpoint('medium')} {
        ${Layout} {
          ${!isFullscreen &&
          mediumLayoutTopBottomPadding &&
          'padding-top: 24px; padding-bottom: 24px'};
        }
      }
    `}

  /* Determine maximum height of content by accounting for Modal Title and optional SelectSlide. */
  height: inherit;
  max-height: ${calculateHeaderHeight('100%')};
  overflow: auto;
  -ms-overflow-style: -ms-autohiding-scrollbar;

  ${breakpoint('medium')} {
    max-height: ${calculateHeaderHeight('95vh')};
    ${({ isFullscreen }) =>
      isFullscreen &&
      css`
        max-height: 100%;
      `}
  }

  ${({ smallLayoutWithSidePadding, smallLayoutTopMargin }) =>
    css`
      ${breakpoint('medium', { type: 'down' })} {
        ${Layout} {
          padding-top: ${smallLayoutTopMargin}px;
          ${smallLayoutWithSidePadding
            ? ''
            : 'padding-left: 0; padding-right: 0;'}
        }
      }
    `}
`;

export const Backdrop = styled.div`
  display: none;

  ${breakpoint('medium')} {
    display: block;
    background: rgba(29, 29, 29, 0.75);
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;

    ${({ isFullscreen }) => isFullscreen && 'display: none;'}
  }
`;

/**
 * This element receives classes applied from CSSTransition
 */
export const TransitionContainer = styled.div`
  position: relative;
  height: ${({ withPeeking }) => (withPeeking ? '100%' : '100vh')};
  z-index: 1301;
  ${Container} {
    transform: translateY(24px);
    opacity: 0;
  }
  &.fade-appear,
  &.fade-enter {
    ${Backdrop} {
      opacity: 0;
    }
  }
  &.fade-appear-active,
  &.fade-enter-active {
    ${Backdrop} {
      transition: all ${({ transitionDuration }) => transitionDuration}ms
        cubic-bezier(0.1, 0.5, 0.1, 1);
      opacity: 1;
    }
    ${Container} {
      transition: all ${({ transitionDuration }) => transitionDuration}ms
        cubic-bezier(0.1, 0.5, 0.1, 1);
      transform: translateY(0);
      opacity: 1;
    }
  }
  &.fade-enter-done {
    ${Backdrop} {
      opacity: 1;
    }
    ${Container} {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.fade-exit {
    ${Backdrop} {
      opacity: 1;
    }
  }
  &.fade-exit-active {
    ${Backdrop} {
      transition: all ${({ transitionDuration }) => transitionDuration}ms
        cubic-bezier(0.1, 0.5, 0.1, 1);
      opacity: 0;
    }
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;
